import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const TeacherDashboard = ({ onNavigate, userInfo }) => {
  const [isMobile, setIsMobile] = useState(false);

  // API Data States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [teacherData, setTeacherData] = useState({
    name: "Loading...",
    teacherId: "...",
    department: "Computer Science",
    designation: "Lecturer",
    profileImage: null
  });

  // Dashboard Data States
  const [dashboardStats, setDashboardStats] = useState({
    totalStudents: 0,
    totalSubjects: 0,
    activeCourses: 0,
    todayClasses: 0
  });

  // NEW: Leave Balance States (Real Data Only)
  const [leaveBalances, setLeaveBalances] = useState({
    casualLeave: 0,
    sickLeave: 0,
    earnedLeave: 0,
    totalLeaves: 0,
    lastUpdated: 'Loading...',
    hasData: false,
    message: '',
    details: {
      casual: { balance: 0, used: 0, allocated: 0 },
      sick: { balance: 0, used: 0, allocated: 0 },
      earned: { balance: 0, used: 0, allocated: 0 }
    }
  });

  // Location and Time Tracking States
  const [locationVerified, setLocationVerified] = useState(false);
  const [locationStatus, setLocationStatus] = useState('');
  const [timeRecords, setTimeRecords] = useState({
    entryTime: null,
    exitTime: null
  });
  const [currentTime, setCurrentTime] = useState(new Date());

  // Dashboard Content States
  const [todaySchedule, setTodaySchedule] = useState([]);
  const [recentAttendance, setRecentAttendance] = useState([]);
  const [notices, setNotices] = useState([]);
  const [events, setEvents] = useState([]);

  // Teacher Individual Attendance States
  const [teacherAttendanceRecords, setTeacherAttendanceRecords] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [todayAttendanceStatus, setTodayAttendanceStatus] = useState(null);

  // Class-wise Attendance States
  const [classAttendanceData, setClassAttendanceData] = useState([]);
  const [selectedSemester, setSelectedSemester] = useState('all');
  const [selectedBatch, setSelectedBatch] = useState('all');
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [classAttendanceLoading, setClassAttendanceLoading] = useState(false);

  // Institution location for reference (Updated coordinates)
  const INSTITUTION_LOCATION = {
    latitude: 22.75875,    // 22°45'31.5"N
    longitude: 88.39605,   // 88°23'45.8"E  
    radius: 0.5            // 500 meters
  };

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);



  // Load today's attendance from localStorage on mount
  useEffect(() => {
    checkTodayAttendanceStatus();
  }, []);

  // Fetch Dashboard Data from API (Updated to include leave balances)
  useEffect(() => {
    const fetchTeacherDashboard = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔍 Starting API call to fetch teacher dashboard data...');

        // Get token from storage
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        console.log('🔑 Token found:', token ? 'Yes' : 'No');

        if (!token) {
          console.log('❌ No token found, using fallback data');
          setError('Authentication token not found. Using fallback data.');
          setTeacherData(prev => ({
            ...prev,
            name: userInfo?.name || "Teacher User",
            teacherId: userInfo?.id || "TCH2024001"
          }));
          setLoading(false);
          return;
        }

        // Construct the full API URL
        const apiUrl = `${API_LINK}/api/teacher/dashboard`;
        console.log('🌐 API URL:', apiUrl);

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('📡 Response status:', response.status);
        console.log('📡 Response ok:', response.ok);

        // Check if response is HTML (404 page) instead of JSON
        const contentType = response.headers.get('content-type');
        console.log('📄 Content-Type:', contentType);

        if (!response.ok) {
          if (response.status === 401) {
            console.log('🔐 Authentication failed');
            setError('Session expired. Please login again.');
            handleLogout();
            return;
          } else if (response.status === 404) {
            console.log('🔍 API endpoint not found');
            setError('API endpoint not found. Please check server configuration.');
            setLoading(false);
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Check if response is JSON
        if (!contentType || !contentType.includes('application/json')) {
          console.log('❌ Response is not JSON:', contentType);
          setError('Server returned non-JSON response. Please check API configuration.');
          setLoading(false);
          return;
        }

        const result = await response.json();
        console.log('📦 API Response:', result);

        if (result.success && result.data) {
          const data = result.data;
          console.log('✅ Processing API data...');

          // Update teacher data
          setTeacherData({
            name: data.teacher?.name || userInfo?.name || "Teacher",
            teacherId: data.teacher?.teacherId || userInfo?.id || "TCH2024001",
            department: data.teacher?.department || "Computer Science",
            designation: data.teacher?.designation || "Lecturer",
            profileImage: data.teacher?.profileImage || null
          });

          // Update dashboard stats
          setDashboardStats({
            totalStudents: data.stats?.totalStudents || 0,
            totalSubjects: data.stats?.totalSubjects || 0,
            activeCourses: data.stats?.activeCourses || 0,
            todayClasses: data.stats?.todayClasses || 0
          });

          // NEW: Update leave balances (only if real data exists)
          if (data.leaveBalances) {
            setLeaveBalances({
              casualLeave: data.leaveBalances.casualLeave || 0,
              sickLeave: data.leaveBalances.sickLeave || 0,
              earnedLeave: data.leaveBalances.earnedLeave || 0,
              totalLeaves: data.leaveBalances.totalLeaves || 0,
              lastUpdated: data.leaveBalances.lastUpdated || 'No data',
              hasData: data.leaveBalances.hasRealData || false,
              message: data.leaveBalances.message || '',
              details: data.leaveBalances.details || {
                casual: { balance: 0, used: 0, allocated: 0 },
                sick: { balance: 0, used: 0, allocated: 0 },
                earned: { balance: 0, used: 0, allocated: 0 }
              }
            });
            console.log('✅ Leave balances updated with real data:', data.leaveBalances);
          } else {
            // No leave balance data available
            setLeaveBalances(prev => ({
              ...prev,
              hasData: false,
              message: 'Leave balance data not available in your profile.'
            }));
          }

          // Update today's schedule
          setTodaySchedule(data.todaySchedule || []);

          // Update recent attendance records
          setRecentAttendance(data.recentAttendance || []);

          // Update time records from API if available
          if (data.teacherAttendance) {
            const entryTime = data.teacherAttendance.entryTime ? {
              time: new Date(data.teacherAttendance.entryTime).toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              }),
              timestamp: new Date(data.teacherAttendance.entryTime).getTime(),
              date: new Date().toISOString().split('T')[0],
              isLate: new Date(data.teacherAttendance.entryTime).getHours() > 11 ||
                (new Date(data.teacherAttendance.entryTime).getHours() === 11 &&
                  new Date(data.teacherAttendance.entryTime).getMinutes() > 0)
            } : null;

            const exitTime = data.teacherAttendance.exitTime ? {
              time: new Date(data.teacherAttendance.exitTime).toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              }),
              timestamp: new Date(data.teacherAttendance.exitTime).getTime(),
              date: new Date().toISOString().split('T')[0]
            } : null;

            setTimeRecords({ entryTime, exitTime });
          }

          // Update notices and events
          setNotices(data.notices || []);
          setEvents(data.events || []);

          // Update class-wise attendance data if available
          if (data.classAttendance && data.classAttendance.length > 0) {
            setClassAttendanceData(data.classAttendance);
          } else {
            // Set sample data if API doesn't provide class attendance data
            setClassAttendanceData([
              {
                subject: 'Data Structures & Algorithms',
                className: 'Computer Science - Year 2',
                semester: '3',
                batch: 'Batch A',
                totalStudents: 45,
                classesHeld: 32,
                avgAttendance: 82
              },
              {
                subject: 'Database Management Systems',
                className: 'Computer Science - Year 2',
                semester: '4',
                batch: 'Batch B',
                totalStudents: 42,
                classesHeld: 28,
                avgAttendance: 75
              },
              {
                subject: 'Operating Systems',
                className: 'Computer Science - Year 3',
                semester: '5',
                batch: 'Batch A',
                totalStudents: 38,
                classesHeld: 25,
                avgAttendance: 68
              },
              {
                subject: 'Computer Networks',
                className: 'Computer Science - Year 3',
                semester: '5',
                batch: 'Batch B',
                totalStudents: 40,
                classesHeld: 30,
                avgAttendance: 78
              },
              {
                subject: 'Web Technologies',
                className: 'Computer Science - Year 2',
                semester: '3',
                batch: 'Batch A',
                totalStudents: 48,
                classesHeld: 35,
                avgAttendance: 88
              },
              {
                subject: 'Software Engineering',
                className: 'Computer Science - Year 3',
                semester: '6',
                batch: 'Batch C',
                totalStudents: 35,
                classesHeld: 22,
                avgAttendance: 55
              }
            ]);
          }

          console.log('✅ Teacher dashboard data updated successfully');

        } else {
          console.log('❌ API returned error:', result.message);
          setError(result.message || 'Failed to fetch dashboard data');
        }

      } catch (error) {
        console.error('💥 Error fetching teacher dashboard data:', error);

        // Handle specific errors
        if (error.name === 'SyntaxError' && error.message.includes('Unexpected token')) {
          setError('Server returned HTML instead of JSON. Please check if the API endpoint exists.');
        } else if (error.message.includes('fetch')) {
          setError('Cannot connect to server. Please ensure the server is running');
        } else {
          setError(`Failed to load dashboard data: ${error.message}`);
        }

        // Fallback to userInfo if available
        if (userInfo) {
          console.log('🔄 Using fallback userInfo data');
          setTeacherData(prev => ({
            ...prev,
            name: userInfo.name || prev.name,
            teacherId: userInfo.id || prev.teacherId,
            designation: userInfo.designation || prev.designation
          }));
        }
      } finally {
        setLoading(false);
      }
    };

    // Add a small delay to ensure component is mounted
    const timer = setTimeout(fetchTeacherDashboard, 500);
    return () => clearTimeout(timer);
  }, [userInfo]);

  // Fetch Teacher's Individual Attendance Records
  useEffect(() => {
    const fetchTeacherAttendanceRecords = async () => {
      try {
        setAttendanceLoading(true);

        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!token) {
          console.log('❌ No token found for attendance records');
          return;
        }

        const response = await fetch(`${API_LINK}/api/teacher/time-records`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            setTeacherAttendanceRecords(result.data.recentRecords || []);
            setTodayAttendanceStatus(result.data.todayRecord || null);
            console.log('✅ Teacher attendance records fetched successfully');
          }
        } else {
          console.log('❌ Failed to fetch teacher attendance records');
        }
      } catch (error) {
        console.error('💥 Error fetching teacher attendance records:', error);
      } finally {
        setAttendanceLoading(false);
      }
    };

    // Fetch attendance records after main dashboard data is loaded
    if (!loading) {
      fetchTeacherAttendanceRecords();
    }
  }, [loading]);

  // Calculate distance between two points (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // STRICT Location Verification - must be on campus to record attendance
  const verifyLocation = () => {
    setLocationStatus('🔍 Verifying your campus location with high precision...');

    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;

          const distance = calculateDistance(
            latitude, longitude,
            INSTITUTION_LOCATION.latitude, INSTITUTION_LOCATION.longitude
          );

          const actualDistanceMeters = distance * 1000;
          const maxDistanceMeters = INSTITUTION_LOCATION.radius * 1000;

          console.log(`📍 Current location: ${latitude}, ${longitude}`);
          console.log(`🏫 Campus location: ${INSTITUTION_LOCATION.latitude}, ${INSTITUTION_LOCATION.longitude}`);
          console.log(`📏 Distance from campus: ${actualDistanceMeters.toFixed(2)} meters`);

          // STRICT location check - block if not on campus
          if (actualDistanceMeters > maxDistanceMeters) {
            setLocationVerified(false);
            setLocationStatus(`❌ LOCATION VERIFICATION FAILED!\n\n🚫 You are ${actualDistanceMeters.toFixed(0)}m away from campus center.\n\n📍 Required: Within ${maxDistanceMeters.toFixed(0)}m of Swami Vivekananda University.\n\n🏃‍♂️ Please move to campus and try again.\n\nNote: You cannot record attendance while off-campus.`);
            sessionStorage.removeItem('locationVerified');
            sessionStorage.removeItem('verifiedLocation');
            return;
          }

          try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');

            const response = await fetch(`${API_LINK}/api/teacher/verify-location`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                latitude, longitude,
                institutionLatitude: INSTITUTION_LOCATION.latitude,
                institutionLongitude: INSTITUTION_LOCATION.longitude,
                maxRadius: INSTITUTION_LOCATION.radius,
                actualDistance: actualDistanceMeters,
                strictMode: true
              })
            });

            const result = await response.json();

            if (result.success && result.data?.locationVerified) {
              setLocationVerified(true);
              setLocationStatus(`✅ LOCATION VERIFIED SUCCESSFULLY!\n\n📍 You are ${actualDistanceMeters.toFixed(0)}m from campus center.\n🎯 Within required campus boundary.\n\n✅ You can now record attendance anytime with flexible timing.`);
              sessionStorage.setItem('locationVerified', 'true');
              sessionStorage.setItem('verifiedLocation', JSON.stringify({
                latitude, longitude, timestamp: Date.now(), distance: actualDistanceMeters
              }));
            } else {
              setLocationVerified(false);
              setLocationStatus(`❌ SERVER LOCATION VERIFICATION FAILED!\n\n🔍 Your GPS location was detected correctly, but server verification failed.\n\n📞 Please contact support if you are physically on campus.\n\nError: ${result.message || 'Unknown server error'}`);
              sessionStorage.removeItem('locationVerified');
            }

          } catch (error) {
            console.error('Location verification API error:', error);
            setLocationVerified(false);
            setLocationStatus(`❌ NETWORK ERROR!\n\n🌐 Cannot verify location with server.\n\n📶 Please check your internet connection and try again.\n\n🚫 Attendance recording is blocked until location is verified.`);
            sessionStorage.removeItem('locationVerified');
          }
        },
        (error) => {
          setLocationVerified(false);
          let errorMessage = '❌ GPS LOCATION ERROR!\n\n';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage += '🚫 Location permission denied.\n\n📍 Please enable location services and allow GPS access.\n\n⚙️ Check your browser/device location settings.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage += '📡 GPS signal unavailable.\n\n🏢 Please ensure you are outdoors or near windows for better GPS signal.\n\n📶 Check your device\'s location services.';
              break;
            case error.TIMEOUT:
              errorMessage += '⏱️ GPS request timed out.\n\n📍 Please ensure you have good GPS signal and try again.\n\n🚶‍♂️ Try moving to an open area.';
              break;
            default:
              errorMessage += '🔧 Unknown GPS error occurred.\n\n🔄 Please try again or restart your device location services.';
          }
          errorMessage += '\n\n🚫 Cannot record attendance without valid location verification.';
          setLocationStatus(errorMessage);
          sessionStorage.removeItem('locationVerified');
          sessionStorage.removeItem('verifiedLocation');
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0 // Always get fresh location
        }
      );
    } else {
      setLocationVerified(false);
      setLocationStatus(`❌ GEOLOCATION NOT SUPPORTED!\n\n🌐 Your browser/device does not support GPS location services.\n\n📱 Please use a modern browser or device with GPS capability.\n\n🚫 Cannot record attendance without location verification.`);
      sessionStorage.removeItem('locationVerified');
    }
  };

  // STRICT Time Recording with mandatory location verification
  const recordTime = async (type) => {
    if (!locationVerified) {
      alert('🚫 LOCATION NOT VERIFIED!\n\nPlease verify your campus location first before recording attendance.\n\nYou must be physically on campus to record attendance.');
      return;
    }

    if (type === 'entry' && timeRecords.entryTime) {
      alert('✅ Entry time already recorded for today.');
      return;
    }

    if (type === 'exit' && (!timeRecords.entryTime || timeRecords.exitTime)) {
      if (!timeRecords.entryTime) {
        alert('⚠️ Please record your entry time first.');
      } else {
        alert('✅ Exit time already recorded for today.');
      }
      return;
    }

    // DOUBLE-CHECK LOCATION before recording (strict enforcement)
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;

          // Verify location again before recording
          const distance = calculateDistance(
            latitude, longitude,
            INSTITUTION_LOCATION.latitude, INSTITUTION_LOCATION.longitude
          );

          const actualDistanceMeters = distance * 1000;
          const maxDistanceMeters = INSTITUTION_LOCATION.radius * 1000;

          console.log(`🔍 Double-checking location for ${type} recording:`);
          console.log(`📍 Current: ${latitude}, ${longitude}`);
          console.log(`📏 Distance: ${actualDistanceMeters.toFixed(2)}m (max: ${maxDistanceMeters}m)`);

          if (actualDistanceMeters > maxDistanceMeters) {
            alert(`❌ ATTENDANCE RECORDING BLOCKED!\n\n🚫 Location verification failed:\nYou are ${actualDistanceMeters.toFixed(0)}m away from campus.\n\n📍 Required: Within ${maxDistanceMeters}m of Swami Vivekananda University.\n\n🏃‍♂️ Please move to campus and verify location again.`);
            setLocationVerified(false);
            sessionStorage.removeItem('locationVerified');
            sessionStorage.removeItem('verifiedLocation');
            return;
          }

          const now = new Date();
          const timeString = now.toLocaleTimeString('en-US', {
            hour: '2-digit', minute: '2-digit', second: '2-digit'
          });

          // Check if entry is after 11 AM (automatic half-day)
          const isLateEntry = type === 'entry' && (now.getHours() > 11 || (now.getHours() === 11 && now.getMinutes() > 0));

          try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');

            // API call with strict location verification
            try {
              const response = await fetch(`${API_LINK}/api/teacher/record-time`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  type,
                  timestamp: now.getTime(),
                  latitude: latitude,
                  longitude: longitude,
                  institutionLatitude: INSTITUTION_LOCATION.latitude,
                  institutionLongitude: INSTITUTION_LOCATION.longitude,
                  distanceFromCampus: actualDistanceMeters,
                  isLateEntry: isLateEntry,
                  timeString: timeString,
                  strictLocationVerified: true
                })
              });

              const result = await response.json();
              if (result.success) {
                console.log('✅ Server recorded time successfully');
              } else {
                throw new Error(result.message || 'Server recording failed');
              }
            } catch (apiError) {
              console.error('API error during time recording:', apiError);
              alert(`❌ NETWORK ERROR!\n\nFailed to record ${type} time on server.\n\n📶 Please check your internet connection and try again.\n\nError: ${apiError.message}`);
              return;
            }

            // Update local state only after successful API call
            const today = new Date().toISOString().split('T')[0];
            const timeRecord = {
              time: timeString,
              timestamp: now.getTime(),
              date: today,
              isLate: isLateEntry,
              location: { latitude, longitude, distance: actualDistanceMeters }
            };

            if (type === 'entry') {
              setTimeRecords(prev => ({ ...prev, entryTime: timeRecord }));

              // Store in localStorage for persistence
              localStorage.setItem('todayEntryTime', JSON.stringify(timeRecord));

              const message = isLateEntry
                ? `✅ ENTRY TIME RECORDED!\n\n⏰ Time: ${timeString}\n📍 Location: ${actualDistanceMeters.toFixed(0)}m from campus\n\n⚠️ HALF DAY STATUS: Entry after 11:00 AM\n\nNote: Your attendance is automatically marked as half day due to late entry.`
                : `✅ ENTRY TIME RECORDED!\n\n⏰ Time: ${timeString}\n📍 Location: ${actualDistanceMeters.toFixed(0)}m from campus\n\n✅ FULL DAY ELIGIBLE: Entry on time\n\nNote: Work at least 8 hours to maintain full day status.`;

              alert(message);

            } else {
              setTimeRecords(prev => ({ ...prev, exitTime: timeRecord }));

              // Calculate final attendance status
              const entryTime = timeRecords.entryTime;
              if (entryTime) {
                const totalHours = (now.getTime() - entryTime.timestamp) / (1000 * 60 * 60);

                let finalStatus = 'FULL DAY';
                let reasons = [];

                if (entryTime.isLate) {
                  finalStatus = 'HALF DAY';
                  reasons.push('Late entry after 11:00 AM');
                }

                if (totalHours < 8) {
                  finalStatus = 'HALF DAY';
                  reasons.push(`Only worked ${totalHours.toFixed(1)} hours (minimum 8 required)`);
                }

                const statusMessage = finalStatus === 'HALF DAY'
                  ? `⚠️ ${finalStatus} ATTENDANCE\n\nReasons:\n• ${reasons.join('\n• ')}`
                  : `✅ ${finalStatus} ATTENDANCE\n\nWorked: ${totalHours.toFixed(1)} hours`;

                // Store final status in localStorage
                localStorage.setItem('todayExitTime', JSON.stringify(timeRecord));
                localStorage.setItem('todayAttendanceStatus', JSON.stringify({
                  status: finalStatus.toLowerCase().replace(' ', '-'),
                  totalHours: totalHours.toFixed(1),
                  reasons: reasons,
                  finalStatus: finalStatus
                }));

                alert(`✅ EXIT TIME RECORDED!\n\n⏰ Time: ${timeString}\n📍 Location: ${actualDistanceMeters.toFixed(0)}m from campus\n⏱️ Total Hours: ${totalHours.toFixed(1)}\n\n${statusMessage}`);

                // Refresh page to show updated status
                setTimeout(() => window.location.reload(), 2000);
              }
            }

          } catch (error) {
            console.error('Time recording error:', error);
            alert(`❌ Error recording ${type} time: ${error.message}\n\nPlease try again or contact support.`);
          }
        },
        (error) => {
          alert(`❌ GPS ERROR!\n\nCannot get your current location for attendance recording.\n\nPlease ensure:\n• GPS is enabled\n• Location permission is granted\n• You have good GPS signal\n\nThen try again.\n\n🚫 Cannot record attendance without location verification.`);
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0 // Always get fresh location for attendance recording
        }
      );
    } else {
      alert(`❌ GEOLOCATION NOT SUPPORTED!\n\nYour device/browser does not support GPS location services.\n\n📱 Please use a modern device with GPS capability.\n\n🚫 Cannot record attendance without location verification.`);
    }
  };

  // Record entry time
  const recordEntryTime = () => recordTime('entry');

  // Record exit time
  const recordExitTime = () => recordTime('exit');

  // Check today's attendance status from localStorage
  const checkTodayAttendanceStatus = () => {
    const today = new Date().toISOString().split('T')[0];
    const entryTime = JSON.parse(localStorage.getItem('todayEntryTime') || 'null');
    const exitTime = JSON.parse(localStorage.getItem('todayExitTime') || 'null');
    const attendanceStatus = JSON.parse(localStorage.getItem('todayAttendanceStatus') || 'null');

    // Check if stored records are for today
    if (entryTime && entryTime.date !== today) {
      localStorage.removeItem('todayEntryTime');
      localStorage.removeItem('todayExitTime');
      localStorage.removeItem('todayAttendanceStatus');
      return null;
    }

    if (entryTime) {
      setTimeRecords(prev => ({
        ...prev,
        entryTime: entryTime,
        exitTime: exitTime
      }));

      if (attendanceStatus) {
        setTodayAttendanceStatus(attendanceStatus);
      }
    }

    return { entryTime, exitTime, attendanceStatus };
  };



  // Handle logout
  const handleLogout = () => {
    console.log('🚪 Logging out...');

    // Clear tokens and location verification
    sessionStorage.clear();
    localStorage.clear();

    // Make logout API call
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      fetch(`${API_LINK}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }).catch(error => console.error('Logout API error:', error));
    }

    if (onNavigate) {
      onNavigate('home');
    } else {
      window.location.href = '/';
    }
  };



  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Format time for display
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const time = new Date(timeString);
    return time.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate total hours worked
  const calculateTotalHours = (entryTime, exitTime) => {
    if (!entryTime || !exitTime) return 'N/A';

    const entry = new Date(entryTime);
    const exit = new Date(exitTime);
    const diffMs = exit - entry;
    const diffHours = diffMs / (1000 * 60 * 60);

    return `${diffHours.toFixed(1)} hrs`;
  };

  // Get attendance status color based on status
  const getAttendanceStatusColor = (status, entryTime, exitTime) => {
    if (!entryTime) return '#ff6b6b'; // Red - Absent
    if (status === 'half-day') return '#ffa500'; // Orange - Half day
    if (status === 'present' && entryTime && exitTime) return '#28a745'; // Green - Full day
    return '#3498db'; // Blue - Pending/In progress
  };

  // Get attendance status display text
  const getAttendanceStatusText = (status, entryTime, exitTime, totalHours) => {
    if (!entryTime) return 'Absent';
    if (status === 'half-day') {
      if (totalHours) {
        return `Half Day (${totalHours}h)`;
      }
      return 'Half Day';
    }
    if (status === 'present' && entryTime && exitTime) {
      if (totalHours) {
        return `Full Day (${totalHours}h)`;
      }
      return 'Full Day';
    }
    if (entryTime && !exitTime) return 'In Progress';
    return 'Pending';
  };

  // Check if entry time is late (after 11 AM)
  const isLateEntry = (entryTime) => {
    if (!entryTime) return false;
    const entry = new Date(entryTime);
    const entryHour = entry.getHours();
    const entryMinutes = entry.getMinutes();
    return entryHour > 11 || (entryHour === 11 && entryMinutes > 0);
  };

  // Check if worked less than 8 hours
  const isUnderworked = (totalHours) => {
    return totalHours && parseFloat(totalHours) < 8;
  };

  // NEW: Get leave status color
  const getLeaveStatusColor = (balance, total) => {
    const percentage = (balance / total) * 100;
    if (percentage >= 70) return '#28a745'; // Green - Good
    if (percentage >= 40) return '#ffa500'; // Orange - Warning
    return '#dc3545'; // Red - Low
  };

  // Loading Component
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Teacher Dashboard...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Error Component
  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem',
        padding: '2rem'
      }}>
        <div style={{
          backgroundColor: '#ffffff',
          padding: '3rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '400px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{
            fontSize: '4rem',
            color: '#ff6b6b',
            marginBottom: '1.5rem'
          }}>
            ⚠️
          </div>

          <h3 style={{
            color: '#333',
            margin: '0 0 1rem 0',
            fontSize: '1.4rem',
            fontWeight: 600
          }}>
            Unable to Load Teacher Dashboard
          </h3>

          <p style={{
            color: '#666',
            margin: '0 0 2rem 0',
            fontSize: '1rem',
            lineHeight: 1.5
          }}>
            For any further information, please contact your support team.
          </p>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: '#238548',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#1e7139';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#238548';
              }}
            >
              Try Again
            </button>

            <button
              onClick={handleLogout}
              style={{
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#555';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#666';
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Layout role="teacher" onNavigate={onNavigate} activePage="teacher-dashboard" userInfo={userInfo}>
      {/* Stats Cards - Updated to include Leave Balance only if real data exists */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: isMobile ? '0.5rem' : '1.5rem',
        marginBottom: isMobile ? '1rem' : '2rem'
      }}>
        {[
          {
            icon: '👥',
            title: 'Total Students',
            value: dashboardStats.totalStudents,
            subtitle: 'Under Your Classes'
          },
          {
            icon: '📚',
            title: 'Subjects',
            value: dashboardStats.totalSubjects,
            subtitle: 'Active Subjects'
          },
          {
            icon: '🏢',
            title: 'Courses',
            value: dashboardStats.activeCourses,
            subtitle: 'Active Courses'
          },
          {
            icon: '📅',
            title: 'Today\'s Classes',
            value: dashboardStats.todayClasses,
            subtitle: 'Scheduled for Today'
          },
          // Only show leave balance card if real data exists
          ...(leaveBalances.hasData ? [{
            icon: '🏖️',
            title: 'Total Leaves',
            value: leaveBalances.totalLeaves,
            subtitle: 'Remaining Balance',
            color: getLeaveStatusColor(leaveBalances.totalLeaves, 39)
          }] : [])
        ].map((stat, index) => (
          <div key={index} style={{
            background: '#ffffff',
            padding: isMobile ? '0.6rem 0.4rem' : '2rem',
            borderRadius: isMobile ? '10px' : '12px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            border: '1px solid #eef2f6',
            textAlign: 'center',
            transition: 'all 0.3s ease'
          }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 6px 18px rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.05)';
            }}>
            <div style={{
              fontSize: isMobile ? '1.3rem' : '2.5rem',
              color: stat.color || '#238548',
              marginBottom: isMobile ? '0.2rem' : '1rem'
            }}>
              {stat.icon}
            </div>
            <h3 style={{
              margin: isMobile ? '0 0 0.1rem 0' : '0 0 0.5rem 0',
              color: '#333',
              fontSize: isMobile ? '0.7rem' : '1rem'
            }}>{stat.title}</h3>
            <p style={{
              fontSize: isMobile ? '1.2rem' : '2.2rem',
              fontWeight: 'bold',
              color: stat.color || '#238548',
              margin: isMobile ? '0.1rem 0' : '0.5rem 0'
            }}>
              {stat.value}
            </p>
            <p style={{
              color: '#666',
              margin: 0,
              fontSize: isMobile ? '0.6rem' : '0.9rem'
            }}>
              {stat.subtitle}
            </p>
          </div>
        ))}
      </section>

      {/* Leave Balance Section - Only show if real data exists */}
      {leaveBalances.hasData ? (
        <section style={{
          background: '#ffffff',
          padding: isMobile ? '0.8rem' : '1.8rem',
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          border: '1px solid #eef2f6',
          marginBottom: isMobile ? '1rem' : '2rem'
        }}>
          <h3 style={{
            color: '#333',
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '0.5rem' : '0.8rem',
            margin: '0 0 0.8rem 0',
            fontSize: isMobile ? '0.95rem' : '1.3rem'
          }}>
            <span style={{ color: '#238548' }}>🏖️</span>
            Leave Balance Details
          </h3>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: isMobile ? '0.4rem' : '1.5rem',
            marginBottom: isMobile ? '0.8rem' : '1.5rem'
          }}>
            {/* Casual Leave */}
            <div style={{
              background: '#f8f9fa',
              padding: isMobile ? '0.5rem 0.25rem' : '1.5rem',
              borderRadius: '8px',
              border: `${isMobile ? '1.5px' : '3px'} solid ${getLeaveStatusColor(leaveBalances.casualLeave, 12)}`,
              transition: 'all 0.3s ease',
              textAlign: 'center'
            }}>
              <h4 style={{ margin: isMobile ? '0 0 0.4rem 0' : '0 0 1rem 0', color: '#333', fontSize: isMobile ? '0.7rem' : '1.1rem' }}>
                🌅 {isMobile ? 'Casual' : 'Casual Leave'}
              </h4>
              <div style={{
                fontSize: isMobile ? '1.3rem' : '2.5rem',
                fontWeight: 'bold',
                color: getLeaveStatusColor(leaveBalances.casualLeave, 12),
                marginBottom: isMobile ? '0.2rem' : '0.5rem'
              }}>
                {leaveBalances.casualLeave}
              </div>
              <div style={{
                fontSize: isMobile ? '0.6rem' : '0.9rem',
                color: '#666'
              }}>
                Remaining
              </div>
            </div>

            {/* Sick Leave */}
            <div style={{
              background: '#f8f9fa',
              padding: isMobile ? '0.5rem 0.25rem' : '1.5rem',
              borderRadius: '8px',
              border: `${isMobile ? '1.5px' : '3px'} solid ${getLeaveStatusColor(leaveBalances.sickLeave, 12)}`,
              transition: 'all 0.3s ease',
              textAlign: 'center'
            }}>
              <h4 style={{ margin: isMobile ? '0 0 0.4rem 0' : '0 0 1rem 0', color: '#333', fontSize: isMobile ? '0.7rem' : '1.1rem' }}>
                🤒 {isMobile ? 'Sick' : 'Sick Leave'}
              </h4>
              <div style={{
                fontSize: isMobile ? '1.3rem' : '2.5rem',
                fontWeight: 'bold',
                color: getLeaveStatusColor(leaveBalances.sickLeave, 12),
                marginBottom: isMobile ? '0.2rem' : '0.5rem'
              }}>
                {leaveBalances.sickLeave}
              </div>
              <div style={{
                fontSize: isMobile ? '0.6rem' : '0.9rem',
                color: '#666'
              }}>
                Remaining
              </div>
            </div>

            {/* Earned Leave */}
            <div style={{
              background: '#f8f9fa',
              padding: isMobile ? '0.5rem 0.25rem' : '1.5rem',
              borderRadius: '8px',
              border: `${isMobile ? '1.5px' : '3px'} solid ${getLeaveStatusColor(leaveBalances.earnedLeave, 15)}`,
              transition: 'all 0.3s ease',
              textAlign: 'center'
            }}>
              <h4 style={{ margin: isMobile ? '0 0 0.4rem 0' : '0 0 1rem 0', color: '#333', fontSize: isMobile ? '0.7rem' : '1.1rem' }}>
                🎯 {isMobile ? 'Earned' : 'Earned Leave'}
              </h4>
              <div style={{
                fontSize: isMobile ? '1.3rem' : '2.5rem',
                fontWeight: 'bold',
                color: getLeaveStatusColor(leaveBalances.earnedLeave, 15),
                marginBottom: isMobile ? '0.2rem' : '0.5rem'
              }}>
                {leaveBalances.earnedLeave}
              </div>
              <div style={{
                fontSize: isMobile ? '0.6rem' : '0.9rem',
                color: '#666'
              }}>
                Remaining
              </div>
            </div>
          </div>

          {/* Leave Summary */}
          <div style={{
            background: leaveBalances.totalLeaves < 15 ? '#ffebee' : leaveBalances.totalLeaves < 25 ? '#fff3cd' : '#e8f5e9',
            padding: isMobile ? '0.6rem' : '1rem',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <p style={{
              margin: isMobile ? '0 0 0.3rem 0' : '0 0 0.5rem 0',
              fontSize: isMobile ? '0.75rem' : '1rem',
              fontWeight: 'bold',
              color: leaveBalances.totalLeaves < 15 ? '#c62828' : leaveBalances.totalLeaves < 25 ? '#856404' : '#2e7d32'
            }}>
              {leaveBalances.totalLeaves < 15 ? '⚠️ Low Leave Balance!' :
                leaveBalances.totalLeaves < 25 ? '📊 Moderate Leave Balance' :
                  '✅ Good Leave Balance'}
            </p>
            <p style={{
              margin: 0,
              fontSize: isMobile ? '0.65rem' : '0.9rem',
              color: '#666'
            }}>
              Total Remaining: <strong>{leaveBalances.totalLeaves} days</strong> •
              Last Updated: {leaveBalances.lastUpdated}
            </p>
            {leaveBalances.totalLeaves < 15 && (
              <p style={{
                margin: '0.3rem 0 0 0',
                fontSize: isMobile ? '0.65rem' : '0.85rem',
                color: '#c62828',
                fontStyle: 'italic'
              }}>
                Consider planning your leaves carefully. Contact HR for leave policy details.
              </p>
            )}
          </div>
        </section>
      ) : leaveBalances.message ? (
        <section style={{
          background: '#ffffff',
          padding: isMobile ? '0.8rem' : '1.8rem',
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          border: '1px solid #eef2f6',
          marginBottom: isMobile ? '1rem' : '2rem',
          textAlign: 'center'
        }}>
          <h3 style={{
            color: '#333',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: isMobile ? '0.4rem' : '0.8rem',
            marginBottom: isMobile ? '0.8rem' : '1.5rem',
            fontSize: isMobile ? '0.95rem' : '1.3rem'
          }}>
            <span style={{ color: '#ffa500' }}>ℹ️</span>
            Leave Balance Information
          </h3>
          <div style={{
            background: '#fff3cd',
            padding: isMobile ? '0.8rem' : '1.5rem',
            borderRadius: '8px',
            border: '1.5px solid #ffc107'
          }}>
            <p style={{
              margin: 0,
              fontSize: isMobile ? '0.75rem' : '1rem',
              color: '#856404'
            }}>
              {leaveBalances.message}
            </p>
          </div>
        </section>
      ) : null}

      {/* Today's Schedule & Recent Attendance */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: isMobile ? '1rem' : '1.5rem',
        marginBottom: isMobile ? '1rem' : '2rem'
      }}>
        {/* Today's Schedule */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          border: '1px solid #eef2f6',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: isMobile ? '0.8rem' : '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '0.6rem' : '1rem'
          }}>
            <span style={{ fontSize: isMobile ? '1.2rem' : '1.8rem' }}>📅</span>
            <h3 style={{ margin: 0, fontSize: isMobile ? '0.9rem' : '1.2rem' }}>Today's Classes</h3>
          </div>
          <div style={{ padding: isMobile ? '0.8rem' : '1.5rem' }}>
            {todaySchedule.length > 0 ? todaySchedule.map((schedule, index) => (
              <div key={index} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: isMobile ? '0.5rem' : '1rem',
                background: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: isMobile ? '0.5rem' : '1rem',
                transition: 'all 0.3s ease'
              }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#e3f2fd';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                }}>
                <div>
                  <div style={{ fontWeight: 'bold', color: '#333', fontSize: isMobile ? '0.8rem' : '1rem' }}>{schedule.timeSlot}</div>
                  <div style={{ color: '#666', fontSize: isMobile ? '0.75rem' : '0.9rem' }}>{schedule.subject}</div>
                  <div style={{ color: '#999', fontSize: isMobile ? '0.65rem' : '0.8rem' }}>
                    {schedule.location} • {schedule.batch}
                  </div>
                </div>
                <div style={{
                  padding: isMobile ? '0.2rem 0.5rem' : '0.5rem 1rem',
                  background: '#238548',
                  color: '#ffffff',
                  borderRadius: '20px',
                  fontSize: isMobile ? '0.65rem' : '0.8rem',
                  fontWeight: 'bold'
                }}>
                  {schedule.studentCount} students
                </div>
              </div>
            )) : (
              <p style={{ textAlign: 'center', color: '#666', padding: isMobile ? '1rem' : '2rem', fontSize: isMobile ? '0.8rem' : '1rem' }}>
                No classes scheduled for today
              </p>
            )}
          </div>
        </div>

        {/* Recent Attendance */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          border: '1px solid #eef2f6',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: isMobile ? '0.8rem' : '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '0.6rem' : '1rem'
          }}>
            <span style={{ fontSize: isMobile ? '1.2rem' : '1.8rem' }}>✅</span>
            <h3 style={{ margin: 0, fontSize: isMobile ? '0.9rem' : '1.2rem' }}>Recent Attendance</h3>
          </div>
          <div style={{ padding: isMobile ? '0.8rem' : '1.5rem' }}>
            {recentAttendance.length > 0 ? recentAttendance.map((record, index) => (
              <div key={index} style={{
                padding: isMobile ? '0.5rem 0' : '1rem',
                borderBottom: index < recentAttendance.length - 1 ? '1px solid #ecf0f1' : 'none',
                transition: 'all 0.3s ease'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 'bold', color: '#333', fontSize: isMobile ? '0.8rem' : '1rem' }}>{record.subject}</div>
                    <div style={{ color: '#666', fontSize: isMobile ? '0.7rem' : '0.9rem' }}>
                      {formatDate(record.date)} • {record.batch}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{
                      padding: isMobile ? '0.15rem 0.4rem' : '0.25rem 0.75rem',
                      borderRadius: '15px',
                      fontSize: isMobile ? '0.7rem' : '0.8rem',
                      fontWeight: 'bold',
                      backgroundColor: record.attendancePercentage >= 75 ? '#e8f5e9' : '#ffebee',
                      color: record.attendancePercentage >= 75 ? '#2e7d32' : '#c62828'
                    }}>
                      {record.attendancePercentage}%
                    </div>
                    <div style={{ color: '#999', fontSize: isMobile ? '0.65rem' : '0.8rem', marginTop: '0.25rem' }}>
                      {record.presentCount}/{record.totalStudents}
                    </div>
                  </div>
                </div>
              </div>
            )) : (
              <p style={{ textAlign: 'center', color: '#666', padding: isMobile ? '1rem' : '2rem', fontSize: isMobile ? '0.8rem' : '1rem' }}>
                No recent attendance records
              </p>
            )}
          </div>
        </div>
      </section>

      {/* STRICT Attendance Management */}
      <section style={{
        background: '#ffffff',
        padding: isMobile ? '0.8rem' : '1.8rem',
        borderRadius: '12px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        border: '1px solid #eef2f6',
        marginBottom: isMobile ? '1rem' : '2rem'
      }}>
        <h3 style={{
          color: '#333',
          display: 'flex',
          alignItems: 'center',
          gap: '0.8rem',
          marginBottom: isMobile ? '0.8rem' : '1.5rem',
          fontSize: isMobile ? '0.95rem' : '1.3rem'
        }}>
          <span style={{ color: '#238548' }}>🛡️</span>
          Strict Campus Attendance System
        </h3>
        <p style={{
          color: '#666',
          marginBottom: isMobile ? '0.8rem' : '1.5rem',
          fontSize: isMobile ? '0.75rem' : '1rem',
          lineHeight: 1.5
        }}>
          <strong>Location-Based Security:</strong> You must be physically on campus to record attendance. Flexible timing with automatic half-day detection: Entry after 11 AM or less than 8 hours work = Half Day.
        </p>

        {/* STRICT Location Verification */}
        {!locationVerified && (
          <div style={{
            background: '#fff3cd',
            padding: isMobile ? '0.8rem' : '1.5rem',
            borderRadius: '8px',
            marginBottom: isMobile ? '0.8rem' : '1.5rem',
            textAlign: 'center',
            border: '1.5px solid #ffc107'
          }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#856404', fontSize: isMobile ? '0.8rem' : '1rem' }}>
              🛡️ CAMPUS LOCATION VERIFICATION REQUIRED
            </h4>
            <p style={{ color: '#856404', marginBottom: '0.8rem', fontSize: isMobile ? '0.75rem' : '0.9rem' }}>
              <strong>Mandatory Security Check:</strong> You must be physically present on Swami Vivekananda University campus to record attendance. GPS verification ensures accurate attendance tracking.
            </p>
            <div style={{
              background: '#f8f9fa',
              padding: isMobile ? '0.6rem' : '1rem',
              borderRadius: '6px',
              marginBottom: isMobile ? '0.8rem' : '1rem',
              border: '1px solid #dee2e6'
            }}>
              <p style={{ color: '#495057', marginBottom: '0.3rem', fontSize: isMobile ? '0.75rem' : '0.9rem', fontWeight: 'bold' }}>
                📍 <strong>Swami Vivekananda University Campus</strong>
              </p>
              <p style={{ color: '#6c757d', marginBottom: '0.3rem', fontSize: isMobile ? '0.7rem' : '0.85rem', fontFamily: 'monospace' }}>
                Coordinates: 22.7591°N, 88.3958°E
              </p>
              <p style={{ color: '#6c757d', margin: 0, fontSize: isMobile ? '0.7rem' : '0.85rem' }}>
                Required Distance: <strong>Within 100 meters</strong> of campus center
              </p>
            </div>
            <p style={{ color: '#856404', marginBottom: '0.4rem', fontSize: isMobile ? '0.75rem' : '0.9rem' }}>
              <strong>🚫 Off-campus attendance recording is strictly prohibited</strong>
            </p>
            <p style={{ color: '#856404', marginBottom: '0.8rem', fontSize: isMobile ? '0.7rem' : '0.85rem' }}>
              The system uses high-precision GPS with server verification to ensure you're physically on campus.
            </p>
            <button
              onClick={verifyLocation}
              style={{
                background: '#238548',
                color: '#ffffff',
                border: 'none',
                padding: isMobile ? '0.5rem 1rem' : '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: isMobile ? '0.8rem' : '1rem',
                fontWeight: '500',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                margin: '0 auto'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#1e7139';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#238548';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              🛡️ Verify Campus Location with GPS
            </button>
            {locationStatus && (
              <div style={{
                marginTop: '0.8rem',
                padding: '0.6rem',
                borderRadius: '6px',
                backgroundColor: locationStatus.includes('✅') ? '#d4edda' : '#f8d7da',
                color: locationStatus.includes('✅') ? '#155724' : '#721c24',
                fontSize: isMobile ? '0.75rem' : '0.9rem',
                fontWeight: '500',
                whiteSpace: 'pre-line',
                textAlign: 'left'
              }}>
                {locationStatus}
              </div>
            )}
          </div>
        )}

        {/* Time Tracking - STRICT LOCATION + FLEXIBLE TIMING */}
        {locationVerified && (
          <div style={{
            background: '#d4edda',
            padding: isMobile ? '0.8rem' : '1.5rem',
            borderRadius: '8px',
            marginBottom: isMobile ? '0.8rem' : '1.5rem',
            border: '1.5px solid #28a745'
          }}>
            <h4 style={{ margin: '0 0 0.8rem 0', color: '#155724', fontSize: isMobile ? '0.85rem' : '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              ✅ Campus Location Verified - Flexible Time Tracking
            </h4>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: isMobile ? '0.8rem' : '1.5rem',
              flexWrap: 'wrap',
              fontSize: isMobile ? '0.8rem' : '1rem',
              gap: '0.5rem'
            }}>
              <div>
                📅 {currentTime.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: isMobile ? '0.95rem' : '1.1rem', fontWeight: 'bold' }}>
                🕐 {currentTime.toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                })}
              </div>
            </div>

            {/* Enhanced Attendance Rules Notice */}
            <div style={{
              background: '#e3f2fd',
              padding: isMobile ? '0.6rem' : '1rem',
              borderRadius: '8px',
              marginBottom: isMobile ? '0.8rem' : '1rem',
              borderLeft: '4px solid #2196f3'
            }}>
              <h5 style={{ margin: '0 0 0.4rem 0', color: '#1976d2', fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                🎯 Smart Attendance Rules (Location Verified)
              </h5>
              <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: isMobile ? '0.75rem' : '0.85rem', color: '#333', lineHeight: 1.4 }}>
                <li><strong>Location:</strong> Must be verified on campus for each recording</li>
                <li><strong>Full Day:</strong> Entry before 11:00 AM + minimum 8 hours work</li>
                <li><strong>Half Day:</strong> Entry after 11:00 AM OR less than 8 hours work</li>
                <li><strong>Flexible Timing:</strong> Record anytime - system auto-detects status</li>
              </ul>
            </div>

            {/* Flexible Status Messages */}
            {!timeRecords.entryTime && currentTime.getHours() > 11 ? (
              <div style={{
                background: '#fff3cd',
                padding: isMobile ? '0.6rem' : '1rem',
                borderRadius: '8px',
                marginBottom: isMobile ? '0.8rem' : '1rem',
                borderLeft: '4px solid #ffc107'
              }}>
                <strong style={{ color: '#856404', fontSize: isMobile ? '0.8rem' : '0.95rem' }}>⚠️ Half Day Notice</strong>
                <p style={{ margin: '0.3rem 0 0 0', fontSize: isMobile ? '0.75rem' : '0.9rem', color: '#856404' }}>
                  Recording entry now will mark attendance as <strong>HALF DAY</strong> (after 11:00 AM).
                </p>
                <p style={{ margin: '0.2rem 0 0 0', fontSize: isMobile ? '0.7rem' : '0.85rem', color: '#856404' }}>
                  Location verification will be checked again before recording.
                </p>
              </div>
            ) : !timeRecords.entryTime ? (
              <div style={{
                background: '#d4edda',
                padding: isMobile ? '0.6rem' : '1rem',
                borderRadius: '8px',
                marginBottom: isMobile ? '0.8rem' : '1rem',
                borderLeft: '4px solid #28a745'
              }}>
                <strong style={{ color: '#155724', fontSize: isMobile ? '0.8rem' : '0.95rem' }}>✅ Perfect Timing</strong>
                <p style={{ margin: '0.3rem 0 0 0', fontSize: isMobile ? '0.75rem' : '0.9rem', color: '#155724' }}>
                  You can record entry for <strong>FULL DAY</strong> eligibility. Work 8+ hours to maintain full day status!
                </p>
              </div>
            ) : timeRecords.entryTime && !timeRecords.exitTime ? (
              <div style={{
                background: '#e3f2fd',
                padding: isMobile ? '0.6rem' : '1rem',
                borderRadius: '8px',
                marginBottom: isMobile ? '0.8rem' : '1rem',
                borderLeft: '4px solid #2196f3'
              }}>
                <strong style={{ color: '#1976d2', fontSize: isMobile ? '0.8rem' : '0.95rem' }}>🏃‍♂️ Work in Progress</strong>
                <p style={{ margin: '0.3rem 0 0 0', fontSize: isMobile ? '0.75rem' : '0.9rem', color: '#1976d2' }}>
                  Entry recorded! {timeRecords.entryTime.isLate ? 'Status: Half Day (late entry)' : 'Work 8+ hours for Full Day status'}
                </p>
                <p style={{ margin: '0.2rem 0 0 0', fontSize: isMobile ? '0.7rem' : '0.85rem', color: '#1976d2' }}>
                  Current work time: {((currentTime.getTime() - timeRecords.entryTime.timestamp) / (1000 * 60 * 60)).toFixed(1)} hours
                </p>
              </div>
            ) : null}

            <div style={{
              display: 'flex',
              gap: isMobile ? '0.5rem' : '1rem',
              marginBottom: isMobile ? '0.8rem' : '1.5rem',
              flexDirection: isMobile ? 'column' : 'row'
            }}>
              <button
                onClick={recordEntryTime}
                disabled={!!timeRecords.entryTime}
                style={{
                  flex: 1,
                  background: timeRecords.entryTime ? '#28a745' : currentTime.getHours() > 11 ? '#ffc107' : '#238548',
                  color: '#ffffff',
                  border: 'none',
                  padding: isMobile ? '0.6rem' : '1rem',
                  borderRadius: '8px',
                  cursor: timeRecords.entryTime ? 'default' : 'pointer',
                  fontSize: isMobile ? '0.8rem' : '1rem',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  opacity: timeRecords.entryTime ? 0.7 : 1
                }}
              >
                {timeRecords.entryTime ? '✓ Entry Recorded' :
                  currentTime.getHours() > 11 ? '⚠️ Record Entry (Half Day)' : '🚪 Record Entry Time'}
              </button>

              <button
                onClick={recordExitTime}
                disabled={!timeRecords.entryTime || !!timeRecords.exitTime}
                style={{
                  flex: 1,
                  background: timeRecords.exitTime ? '#28a745' : '#3498db',
                  color: '#ffffff',
                  border: 'none',
                  padding: isMobile ? '0.6rem' : '1rem',
                  borderRadius: '8px',
                  cursor: (!timeRecords.entryTime || timeRecords.exitTime) ? 'default' : 'pointer',
                  fontSize: isMobile ? '0.8rem' : '1rem',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  opacity: (!timeRecords.entryTime || timeRecords.exitTime) ? 0.7 : 1
                }}
              >
                {timeRecords.exitTime ? '✓ Exit Recorded' : '🚪 Record Exit Time'}
              </button>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: isMobile ? '0.5rem' : '1rem'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: isMobile ? '0.5rem' : '0.8rem',
                background: '#ffffff',
                borderRadius: '6px',
                fontSize: isMobile ? '0.75rem' : '0.9rem',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}>
                <span style={{ fontWeight: '500' }}>Entry:</span>
                <span style={{ fontFamily: 'monospace' }}>
                  {timeRecords.entryTime ? timeRecords.entryTime.time : 'N/A'}
                </span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: isMobile ? '0.5rem' : '0.8rem',
                background: '#ffffff',
                borderRadius: '6px',
                fontSize: isMobile ? '0.75rem' : '0.9rem',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}>
                <span style={{ fontWeight: '500' }}>Exit:</span>
                <span style={{ fontFamily: 'monospace' }}>
                  {timeRecords.exitTime ? timeRecords.exitTime.time : 'N/A'}
                </span>
              </div>
            </div>

            {/* Today's Status Summary */}
            {todayAttendanceStatus && (
              <div style={{
                marginTop: isMobile ? '0.8rem' : '1rem',
                padding: isMobile ? '0.6rem' : '1rem',
                background: '#ffffff',
                borderRadius: '8px',
                boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
                borderLeft: `4px solid ${todayAttendanceStatus.status === 'half-day' ? '#ffc107' : '#28a745'}`
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <strong style={{ color: '#333', fontSize: isMobile ? '0.75rem' : '0.95rem' }}>Today's Final Status:</strong>
                    <div style={{
                      marginTop: '0.2rem',
                      fontSize: isMobile ? '0.7rem' : '0.9rem',
                      color: '#666'
                    }}>
                      {todayAttendanceStatus.totalHours && `Total Hours: ${todayAttendanceStatus.totalHours}`}
                      {todayAttendanceStatus.reasons && todayAttendanceStatus.reasons.length > 0 && (
                        <div style={{ marginTop: '0.15rem', fontSize: isMobile ? '0.65rem' : '0.85rem' }}>
                          Reasons: {todayAttendanceStatus.reasons.join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                  <span style={{
                    padding: isMobile ? '0.2rem 0.5rem' : '0.5rem 1rem',
                    borderRadius: '20px',
                    fontSize: isMobile ? '0.75rem' : '0.9rem',
                    fontWeight: 'bold',
                    backgroundColor: todayAttendanceStatus.status === 'half-day' ? '#fff3cd' : '#d4edda',
                    color: todayAttendanceStatus.status === 'half-day' ? '#856404' : '#155724'
                  }}>
                    {todayAttendanceStatus.finalStatus || (todayAttendanceStatus.status === 'half-day' ? 'HALF DAY' : 'FULL DAY')}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        <button
          onClick={() => onNavigate('teacher-attendance')}
          style={{
            background: '#238548',
            color: '#ffffff',
            border: 'none',
            padding: isMobile ? '0.5rem 1rem' : '0.75rem 1.5rem',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: isMobile ? '0.8rem' : '1rem',
            fontWeight: '500',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#3498db';
            e.target.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = '#238548';
            e.target.style.transform = 'translateY(0)';
          }}
        >
          ✅ {locationVerified ? 'Manage Student Attendance' : 'Verify Campus Location First'}
        </button>
      </section>

      <section style={{
        background: '#ffffff',
        padding: isMobile ? '0.8rem 0.4rem' : '1.8rem',
        borderRadius: '12px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        border: '1px solid #eef2f6',
        marginBottom: isMobile ? '1rem' : '2rem'
      }}>
        <h3 style={{
          color: '#333',
          display: 'flex',
          alignItems: 'center',
          gap: '0.8rem',
          marginBottom: isMobile ? '0.8rem' : '1.5rem',
          fontSize: isMobile ? '0.95rem' : '1.3rem'
        }}>
          <span style={{ color: '#238548' }}>📊</span>
          My Attendance Records
        </h3>
        <p style={{
          color: '#666',
          marginBottom: isMobile ? '0.8rem' : '1.5rem',
          fontSize: isMobile ? '0.75rem' : '1rem',
          lineHeight: 1.5
        }}>
          Complete history of your campus-verified attendance with smart status tracking. Location verification is mandatory, but timing is flexible. The system automatically determines Full Day or Half Day based on entry time and total hours worked.
        </p>

        {attendanceLoading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '3rem',
            color: '#666',
            fontSize: isMobile ? '0.8rem' : '1rem'
          }}>
            <div style={{
              width: '30px',
              height: '30px',
              border: '3px solid #e3f2fd',
              borderTop: '3px solid #238548',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginRight: '1rem'
            }}></div>
            Loading attendance records...
          </div>
        ) : teacherAttendanceRecords.length > 0 ? (
          <div style={{
            overflowX: 'auto',
            margin: isMobile ? '0 -0.4rem' : '0',
            padding: isMobile ? '0 0.4rem' : '0'
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: isMobile ? '0.7rem' : '0.9rem'
            }}>
              <thead>
                <tr style={{
                  backgroundColor: '#f8f9fa',
                  borderBottom: '2px solid #e9ecef'
                }}>
                  <th style={{
                    padding: isMobile ? '0.5rem 0.25rem' : '1rem',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#333',
                    whiteSpace: 'nowrap'
                  }}>Date</th>
                  <th style={{
                    padding: isMobile ? '0.5rem 0.25rem' : '1rem',
                    textAlign: 'center',
                    fontWeight: '600',
                    color: '#333',
                    whiteSpace: 'nowrap'
                  }}>Entry Time</th>
                  <th style={{
                    padding: isMobile ? '0.5rem 0.25rem' : '1rem',
                    textAlign: 'center',
                    fontWeight: '600',
                    color: '#333',
                    whiteSpace: 'nowrap'
                  }}>Exit Time</th>
                  <th style={{
                    padding: isMobile ? '0.5rem 0.25rem' : '1rem',
                    textAlign: 'center',
                    fontWeight: '600',
                    color: '#333',
                    whiteSpace: 'nowrap'
                  }}>Total Hours</th>
                  <th style={{
                    padding: isMobile ? '0.5rem 0.25rem' : '1rem',
                    textAlign: 'center',
                    fontWeight: '600',
                    color: '#333',
                    whiteSpace: 'nowrap'
                  }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {teacherAttendanceRecords.map((record, index) => (
                  <tr key={index} style={{
                    borderBottom: '1px solid #e9ecef',
                    transition: 'all 0.3s ease'
                  }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}>
                    <td style={{
                      padding: isMobile ? '0.5rem 0.25rem' : '1rem',
                      fontWeight: '500',
                      color: '#333',
                      whiteSpace: 'nowrap',
                      textAlign: 'left'
                    }}>
                      {formatDate(record.date)}
                    </td>
                    <td style={{
                      padding: isMobile ? '0.5rem 0.25rem' : '1rem',
                      textAlign: 'center',
                      fontFamily: 'monospace',
                      whiteSpace: 'nowrap',
                      color: record.entryTime ? (isLateEntry(record.entryTime) ? '#ffa500' : '#238548') : '#ff6b6b'
                    }}>
                      {formatTime(record.entryTime)}
                      {record.entryTime && isLateEntry(record.entryTime) && (
                        <div style={{ fontSize: '0.6rem', color: '#ffa500', marginTop: '0.1rem' }}>
                          Late Entry
                        </div>
                      )}
                    </td>
                    <td style={{
                      padding: isMobile ? '0.5rem 0.25rem' : '1rem',
                      textAlign: 'center',
                      fontFamily: 'monospace',
                      whiteSpace: 'nowrap',
                      color: record.exitTime ? '#238548' : '#ffa500'
                    }}>
                      {formatTime(record.exitTime)}
                    </td>
                    <td style={{
                      padding: isMobile ? '0.5rem 0.25rem' : '1rem',
                      textAlign: 'center',
                      fontFamily: 'monospace',
                      fontWeight: '500',
                      whiteSpace: 'nowrap',
                      color: isUnderworked(record.totalHours) ? '#ffa500' : '#333'
                    }}>
                      {calculateTotalHours(record.entryTime, record.exitTime)}
                      {record.totalHours && isUnderworked(record.totalHours) && (
                        <div style={{ fontSize: '0.6rem', color: '#ffa500', marginTop: '0.1rem' }}>
                          Under 8hrs
                        </div>
                      )}
                    </td>
                    <td style={{
                      padding: isMobile ? '0.5rem 0.25rem' : '1rem',
                      textAlign: 'center',
                      whiteSpace: 'nowrap'
                    }}>
                      <span style={{
                        padding: isMobile ? '0.15rem 0.25rem' : '0.25rem 0.75rem',
                        borderRadius: '20px',
                        fontSize: isMobile ? '0.6rem' : '0.8rem',
                        fontWeight: 'bold',
                        backgroundColor: (() => {
                          switch (record.status) {
                            case 'present': return '#e8f5e9';
                            case 'half-day': return '#fff3cd';
                            case 'absent': return '#ffebee';
                            default: return '#f0f0f0';
                          }
                        })(),
                        color: (() => {
                          switch (record.status) {
                            case 'present': return '#2e7d32';
                            case 'half-day': return '#856404';
                            case 'absent': return '#c62828';
                            default: return '#666';
                          }
                        })()
                      }}>
                        {getAttendanceStatusText(record.status, record.entryTime, record.exitTime, record.totalHours)}
                      </span>
                      {/* Show auto-detection reasons for half-day */}
                      {record.status === 'half-day' && (
                        <div style={{
                          marginTop: '0.1rem',
                          fontSize: '0.6rem',
                          color: '#856404'
                        }}>
                          {isLateEntry(record.entryTime) && '⏰ Late'}
                          {isLateEntry(record.entryTime) && isUnderworked(record.totalHours) && ' • '}
                          {isUnderworked(record.totalHours) && '⏱️ <8h'}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Attendance Summary */}
            <div style={{
              marginTop: '1.2rem',
              padding: '0.8rem',
              background: '#f8f9fa',
              borderRadius: '8px',
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(4, 1fr)' : 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: isMobile ? '0.3rem' : '1rem'
            }}>
              {(() => {
                const stats = teacherAttendanceRecords.reduce((acc, record) => {
                  if (record.status === 'present') acc.fullDays++;
                  else if (record.status === 'half-day') acc.halfDays++;
                  else if (!record.entryTime) acc.absent++;
                  return acc;
                }, { fullDays: 0, halfDays: 0, absent: 0 });

                return [
                  { label: 'Full Days', value: stats.fullDays, color: '#28a745' },
                  { label: 'Half Days', value: stats.halfDays, color: '#ffa500' },
                  { label: 'Absent', value: stats.absent, color: '#dc3545' },
                  { label: 'Total Records', value: teacherAttendanceRecords.length, color: '#6c757d' }
                ].map((stat, index) => (
                  <div key={index} style={{
                    textAlign: 'center',
                    padding: isMobile ? '0.2rem 0' : '0.5rem'
                  }}>
                    <div style={{
                      fontSize: isMobile ? '1.1rem' : '1.5rem',
                      fontWeight: 'bold',
                      color: stat.color
                    }}>
                      {stat.value}
                    </div>
                    <div style={{
                      fontSize: isMobile ? '0.6rem' : '0.8rem',
                      color: '#666',
                      fontWeight: '500'
                    }}>
                      {stat.label}
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            color: '#666'
          }}>
            <div style={{
              fontSize: '3rem',
              marginBottom: '1rem',
              opacity: 0.5
            }}>
              📊
            </div>
            <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
              No attendance records found
            </p>
            <p style={{ fontSize: '0.9rem', opacity: 0.7 }}>
              Verify your campus location and start recording your entry/exit times to build your attendance history.
            </p>
          </div>
        )}
      </section>

      {/* Class-wise Attendance Per Semester */}
      <section style={{
        background: '#ffffff',
        padding: isMobile ? '0.8rem' : '1.8rem',
        borderRadius: '12px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        border: '1px solid #eef2f6',
        marginBottom: isMobile ? '1rem' : '2rem'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: isMobile ? '0.8rem' : '1.5rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <h3 style={{
            color: '#333',
            display: 'flex',
            alignItems: 'center',
            gap: '0.8rem',
            margin: 0,
            fontSize: isMobile ? '0.95rem' : '1.3rem'
          }}>
            <span style={{ color: '#238548' }}>📚</span>
            Class-wise Attendance Records
          </h3>

          {/* Filter Controls */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: isMobile ? '0.5rem' : '1rem'
          }}>
            {/* Semester Filter */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              fontSize: isMobile ? '0.75rem' : '0.9rem'
            }}>
              <label style={{ color: '#666', fontWeight: '500' }}>Sem:</label>
              <select
                value={selectedSemester}
                onChange={(e) => setSelectedSemester(e.target.value)}
                style={{
                  padding: isMobile ? '0.3rem 0.5rem' : '0.5rem 1rem',
                  borderRadius: '6px',
                  border: '1.5px solid #97d6ac',
                  background: '#ffffff',
                  color: '#333',
                  fontSize: isMobile ? '0.75rem' : '0.95rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                <option value="all">All Semesters</option>
                <option value="1">Semester 1</option>
                <option value="2">Semester 2</option>
                <option value="3">Semester 3</option>
                <option value="4">Semester 4</option>
                <option value="5">Semester 5</option>
                <option value="6">Semester 6</option>
                <option value="7">Semester 7</option>
                <option value="8">Semester 8</option>
              </select>
            </div>

            {/* Batch Filter */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              fontSize: isMobile ? '0.75rem' : '0.9rem'
            }}>
              <label style={{ color: '#666', fontWeight: '500' }}>Batch:</label>
              <select
                value={selectedBatch}
                onChange={(e) => setSelectedBatch(e.target.value)}
                style={{
                  padding: isMobile ? '0.3rem 0.5rem' : '0.5rem 1rem',
                  borderRadius: '6px',
                  border: '1.5px solid #97d6ac',
                  background: '#ffffff',
                  color: '#333',
                  fontSize: isMobile ? '0.75rem' : '0.95rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                <option value="all">All Batches</option>
                <option value="Batch A">Batch A</option>
                <option value="Batch B">Batch B</option>
                <option value="Batch C">Batch C</option>
              </select>
            </div>

            {/* Subject Filter */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              fontSize: isMobile ? '0.75rem' : '0.9rem'
            }}>
              <label style={{ color: '#666', fontWeight: '500' }}>Subject:</label>
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                style={{
                  padding: isMobile ? '0.3rem 0.5rem' : '0.5rem 1rem',
                  borderRadius: '6px',
                  border: '1.5px solid #97d6ac',
                  background: '#ffffff',
                  color: '#333',
                  fontSize: isMobile ? '0.75rem' : '0.95rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                <option value="all">All Subjects</option>
                <option value="Data Structures & Algorithms">Data Structures & Algorithms</option>
                <option value="Database Management Systems">Database Management Systems</option>
                <option value="Operating Systems">Operating Systems</option>
                <option value="Computer Networks">Computer Networks</option>
                <option value="Web Technologies">Web Technologies</option>
                <option value="Software Engineering">Software Engineering</option>
              </select>
            </div>
          </div>
        </div>

        <p style={{
          color: '#666',
          marginBottom: isMobile ? '0.8rem' : '1.5rem',
          fontSize: isMobile ? '0.75rem' : '1rem',
          lineHeight: 1.5
        }}>
          View attendance statistics for each class you teach. Use the filters above to narrow down by semester, batch, or subject. Track student participation and attendance patterns across different courses.
        </p>

        {classAttendanceLoading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '3rem',
            color: '#666',
            fontSize: isMobile ? '0.8rem' : '1rem'
          }}>
            <div style={{
              width: '30px',
              height: '30px',
              border: '3px solid #e3f2fd',
              borderTop: '3px solid #238548',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginRight: '1rem'
            }}></div>
            Loading class attendance data...
          </div>
        ) : (() => {
          // Filter data by semester, batch, and subject
          let filteredData = classAttendanceData;

          if (selectedSemester !== 'all') {
            filteredData = filteredData.filter(item => item.semester === selectedSemester);
          }

          if (selectedBatch !== 'all') {
            filteredData = filteredData.filter(item => item.batch === selectedBatch);
          }

          if (selectedSubject !== 'all') {
            filteredData = filteredData.filter(item => item.subject === selectedSubject);
          }

          return filteredData.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: isMobile ? '0.8rem' : '1.5rem'
            }}>
              {filteredData.map((classData, index) => (
                <div key={index} style={{
                  background: '#f8f9fa',
                  padding: isMobile ? '0.8rem' : '1.5rem',
                  borderRadius: '10px',
                  border: '1.5px solid #e9ecef',
                  transition: 'all 0.3s ease'
                }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.06)';
                    e.currentTarget.style.borderColor = '#97d6ac';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = '#e9ecef';
                  }}>
                  {/* Class Header */}
                  <div style={{
                    marginBottom: isMobile ? '0.6rem' : '1rem',
                    paddingBottom: isMobile ? '0.6rem' : '1rem',
                    borderBottom: '1.5px solid #dee2e6'
                  }}>
                    <h4 style={{
                      margin: '0 0 0.4rem 0',
                      color: '#238548',
                      fontSize: isMobile ? '0.85rem' : '1.1rem',
                      fontWeight: 'bold'
                    }}>
                      {classData.subject}
                    </h4>
                    <div style={{
                      display: 'flex',
                      gap: isMobile ? '0.3rem 0.6rem' : '1rem',
                      flexWrap: 'wrap',
                      fontSize: isMobile ? '0.7rem' : '0.85rem',
                      color: '#666'
                    }}>
                      <span>📚 {classData.className}</span>
                      <span>📅 Sem {classData.semester}</span>
                      <span>🎓 {classData.batch}</span>
                      <span>👥 {classData.totalStudents} students</span>
                    </div>
                  </div>

                  {/* Attendance Stats */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: isMobile ? '0.5rem' : '1rem',
                    marginBottom: isMobile ? '0.6rem' : '1rem'
                  }}>
                    <div style={{
                      background: '#ffffff',
                      padding: isMobile ? '0.5rem' : '1rem',
                      borderRadius: '8px',
                      textAlign: 'center',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                    }}>
                      <div style={{
                        fontSize: isMobile ? '1.3rem' : '1.8rem',
                        fontWeight: 'bold',
                        color: '#238548',
                        marginBottom: '0.1rem'
                      }}>
                        {classData.classesHeld}
                      </div>
                      <div style={{
                        fontSize: isMobile ? '0.65rem' : '0.85rem',
                        color: '#666',
                        fontWeight: '500'
                      }}>
                        Classes Held
                      </div>
                    </div>

                    <div style={{
                      background: '#ffffff',
                      padding: isMobile ? '0.5rem' : '1rem',
                      borderRadius: '8px',
                      textAlign: 'center',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                    }}>
                      <div style={{
                        fontSize: isMobile ? '1.3rem' : '1.8rem',
                        fontWeight: 'bold',
                        color: classData.avgAttendance >= 75 ? '#28a745' : classData.avgAttendance >= 60 ? '#ffa500' : '#dc3545',
                        marginBottom: '0.1rem'
                      }}>
                        {classData.avgAttendance}%
                      </div>
                      <div style={{
                        fontSize: isMobile ? '0.65rem' : '0.85rem',
                        color: '#666',
                        fontWeight: '500'
                      }}>
                        Avg Attendance
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div style={{
                    background: '#e9ecef',
                    height: isMobile ? '5px' : '8px',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    marginBottom: isMobile ? '0.4rem' : '0.5rem'
                  }}>
                    <div style={{
                      width: `${classData.avgAttendance}%`,
                      height: '100%',
                      background: classData.avgAttendance >= 75 ? 'linear-gradient(90deg, #28a745, #20c997)' :
                        classData.avgAttendance >= 60 ? 'linear-gradient(90deg, #ffa500, #ffb732)' :
                          'linear-gradient(90deg, #dc3545, #e74c3c)',
                      transition: 'width 0.5s ease'
                    }}></div>
                  </div>

                  {/* Status Badge */}
                  <div style={{
                    textAlign: 'center',
                    marginTop: '0.3rem'
                  }}>
                    <span style={{
                      display: 'inline-block',
                      padding: isMobile ? '0.2rem 0.5rem' : '0.4rem 1rem',
                      borderRadius: '20px',
                      fontSize: isMobile ? '0.7rem' : '0.8rem',
                      fontWeight: 'bold',
                      background: classData.avgAttendance >= 75 ? '#e8f5e9' : classData.avgAttendance >= 60 ? '#fff3cd' : '#ffebee',
                      color: classData.avgAttendance >= 75 ? '#2e7d32' : classData.avgAttendance >= 60 ? '#856404' : '#c62828'
                    }}>
                      {classData.avgAttendance >= 75 ? '✓ Good Attendance' :
                        classData.avgAttendance >= 60 ? '⚠ Needs Improvement' :
                          '⚠ Poor Attendance'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: '#666'
            }}>
              <div style={{
                fontSize: '3rem',
                marginBottom: '1rem',
                opacity: 0.5
              }}>
                📚
              </div>
              <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                No class attendance data found
              </p>
              <p style={{ fontSize: '0.9rem', opacity: 0.7 }}>
                {selectedSemester !== 'all' || selectedBatch !== 'all' || selectedSubject !== 'all'
                  ? 'No attendance records found for the selected filters. Try adjusting your filter criteria.'
                  : 'No attendance records available for your classes yet.'}
              </p>
            </div>
          );
        })()}
      </section>

      {/* Important Notices & Upcoming Events */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: isMobile ? '1rem' : '1.5rem'
      }}>
        {/* Important Notices */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          border: '1px solid #eef2f6',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: isMobile ? '0.8rem' : '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '0.6rem' : '1rem'
          }}>
            <span style={{ fontSize: isMobile ? '1.2rem' : '1.8rem' }}>📋</span>
            <h3 style={{ margin: 0, fontSize: isMobile ? '0.9rem' : '1.2rem' }}>Important Notices</h3>
          </div>
          <div style={{ padding: isMobile ? '0.8rem' : '1.5rem' }}>
            {notices.length > 0 ? notices.map((notice, index) => (
              <div key={index} style={{
                padding: isMobile ? '0.5rem 0' : '1rem',
                borderBottom: index < notices.length - 1 ? '1px solid #ecf0f1' : 'none',
                transition: 'all 0.3s ease'
              }}>
                <h4 style={{ margin: '0 0 0.3rem 0', color: '#333', fontSize: isMobile ? '0.8rem' : '1rem' }}>{notice.title}</h4>
                <p style={{ margin: '0 0 0.4rem 0', color: '#666', fontSize: isMobile ? '0.75rem' : '0.9rem', lineHeight: 1.4 }}>
                  {notice.content}
                </p>
                <div style={{
                  margin: 0,
                  fontSize: isMobile ? '0.7rem' : '0.8rem',
                  color: '#999',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <span>{formatDate(notice.date)}</span>
                  <span style={{
                    padding: isMobile ? '0.15rem 0.4rem' : '0.25rem 0.75rem',
                    borderRadius: '15px',
                    fontSize: isMobile ? '0.7rem' : '0.8rem',
                    fontWeight: 500,
                    ...(notice.type === 'important' ? {
                      background: '#3498db',
                      color: '#ffffff'
                    } : {
                      background: '#bdc3c7',
                      color: '#333'
                    })
                  }}>
                    {notice.type}
                  </span>
                </div>
              </div>
            )) : (
              <p style={{ textAlign: 'center', color: '#666', padding: isMobile ? '1rem' : '2rem', fontSize: isMobile ? '0.8rem' : '1rem' }}>
                No notices available
              </p>
            )}
          </div>
        </div>

        {/* Upcoming Events */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          border: '1px solid #eef2f6',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: isMobile ? '0.8rem' : '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '0.6rem' : '1rem'
          }}>
            <span style={{ fontSize: isMobile ? '1.2rem' : '1.8rem' }}>📅</span>
            <h3 style={{ margin: 0, fontSize: isMobile ? '0.9rem' : '1.2rem' }}>Upcoming Events</h3>
          </div>
          <div style={{ padding: isMobile ? '0.8rem' : '1.5rem' }}>
            {events.length > 0 ? events.map((event, index) => (
              <div key={index} style={{
                display: 'flex',
                gap: isMobile ? '0.8rem' : '1.5rem',
                padding: isMobile ? '0.8rem 0' : '1.5rem',
                borderBottom: index < events.length - 1 ? '1px solid #ecf0f1' : 'none',
                transition: 'all 0.3s ease'
              }}>
                <div style={{
                  background: '#238548',
                  color: '#ffffff',
                  padding: isMobile ? '0.4rem' : '0.75rem',
                  borderRadius: '8px',
                  fontSize: isMobile ? '0.75rem' : '0.9rem',
                  textAlign: 'center',
                  minWidth: isMobile ? '60px' : '80px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  height: 'fit-content'
                }}>
                  <div style={{ fontWeight: 'bold', fontSize: isMobile ? '1rem' : '1.2rem' }}>{new Date(event.date).getDate()}</div>
                  <div style={{ fontSize: isMobile ? '0.65rem' : '0.8rem' }}>
                    {new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: '0 0 0.3rem 0', color: '#333', fontSize: isMobile ? '0.8rem' : '1rem' }}>{event.title}</h4>
                  <p style={{
                    margin: '0 0 0.4rem 0',
                    color: '#666',
                    fontSize: isMobile ? '0.75rem' : '0.9rem',
                    lineHeight: 1.4
                  }}>
                    {event.description}
                  </p>
                  <p style={{
                    margin: '0 0 0.4rem 0',
                    color: '#666',
                    fontSize: isMobile ? '0.75rem' : '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? '0.4rem 0.8rem' : '1rem',
                    flexWrap: 'wrap'
                  }}>
                    <span>🕐 {event.time}</span>
                    <span>📍 {event.location}</span>
                  </p>
                  <span style={{
                    display: 'inline-block',
                    padding: isMobile ? '0.15rem 0.5rem' : '0.25rem 0.75rem',
                    background: '#f4f7fa',
                    color: '#666',
                    borderRadius: '15px',
                    fontSize: isMobile ? '0.7rem' : '0.8rem',
                    fontWeight: 500
                  }}>
                    {event.type}
                  </span>
                </div>
              </div>
            )) : (
              <p style={{ textAlign: 'center', color: '#666', padding: isMobile ? '1rem' : '2rem', fontSize: isMobile ? '0.8rem' : '1rem' }}>
                No upcoming events
              </p>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TeacherDashboard;